<template>
  <div v-if="timeZoneSelected" id="timezone">
    <!-- the same compact menu as /2026's Key Dates (TimeZoneInline), 2026-10-13: up to three
         zones (selectedTimeZones.js), then every other zone by region (TimeZoneMenu, 2026-10-14) -->
    <TimeZoneInline
      variant="compact"
      :event-zone="timeZoneConference"
      :year="route.params.year || ''"
      @change="switchTimeZone" />
  </div>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line
const props = defineProps(["timeZoneSelected", "daySelected"]);
import timezoneHistory from "@/assets/history.json";
const {DateTime} = require("luxon");
import TimeZoneInline from "@/components/TimeZoneInline.vue";
import {useUrlHash} from "@/composables/useSetUrlHash";
const {setUrlHash} = useUrlHash();

import {computed, ref, watch} from "vue";
import useUserSafe from "@/composables/useUserSafe";
import {
  getPreferredTimeZone, getSelectedTimeZones, saveSelectedTimeZone,
  setStoredTimeZone
} from "@/composables/selectedTimeZones";
import {useRoute} from "vue-router";
const route = useRoute();
const timeZoneConference = computed(() => {
  const year = route.params.year;
  if (year) return timezoneHistory.years[year];
  return "";
});

const emit = defineEmits(["update"]);
const {user} = useUserSafe();
const isFromHash = ref(false); // a link's zone wins over the account's last choice
const isInitialised = ref(false);

const getNormalizedDayForHash = (day) => {
  if (!day) return "";
  return String(day).trim().replace(/\s+/g, "-");
};

const getTimeZoneTokenForHash = (timeZone) => {
  if (!timeZone) return "";
  return String(timeZone).replace("/", "-");
};

function switchTimeZone(newTimeZone) {
  log(`switchTimeZone(${newTimeZone})`);
  emit("update", newTimeZone);
  setStoredTimeZone(newTimeZone);
  updateUrlHash(props.daySelected, newTimeZone);
  saveSelectedTimeZone(user.value, newTimeZone); // signed in: remembered on every device
}

(async function () {
  log("Immediately invoke function to set initial TimeZone based on URL hash");
  let parameters = route.hash.replace(/^#/, "").split("&").filter(Boolean);
  const first = parameters[0] || "";
  const firstDecoded = decodeURIComponent(first);
  const firstHasDigit = /\d/.test(firstDecoded);
  if (route.name === "Program" && firstHasDigit) parameters.shift();
  // unrelated tokens (e.g. "#profile") must not be mistaken for a time zone
  const isValidTimeZone = (zone) => DateTime.local().setZone(zone).isValid;
  const hashTimeZoneToken = parameters[0] || "";
  if (hashTimeZoneToken) {
    const decoded = decodeURIComponent(hashTimeZoneToken);
    const newTimeZone = decoded.replace("-", "/");
    if (isValidTimeZone(newTimeZone)) {
      log(`Set time zone from URL hash: ${newTimeZone}`);
      emit("update", newTimeZone);
      setStoredTimeZone(newTimeZone);
      isFromHash.value = true;
      isInitialised.value = true;
      return;
    }
    log(`Ignoring invalid time zone in URL hash: ${newTimeZone}`);
  }
  // the account's last choice, else this browser's, else the browser's own zone (the account's
  // choice may arrive later, once Clerk has loaded: the watcher below)
  const newTimeZone = getPreferredTimeZone(user.value);
  log(`Set the preferred time zone: ${newTimeZone}`);
  emit("update", newTimeZone);
  updateUrlHash(props.daySelected, newTimeZone);
  isInitialised.value = true;
})();

// signed in (once Clerk has loaded): the account's last choice, on any device, unless a link
// gave a zone
watch([() => user.value, isInitialised], ([currentUser, initialised]) => {
  const [lastChosen] = getSelectedTimeZones(currentUser);
  if (!initialised || isFromHash.value || !lastChosen) return;
  if (lastChosen === props.timeZoneSelected) return;
  log(`Set time zone from the account's last choice: ${lastChosen}`);
  emit("update", lastChosen);
  setStoredTimeZone(lastChosen);
  updateUrlHash(props.daySelected, lastChosen);
}, {immediate: true});

function updateUrlHash(day, timeZone) {
  log(`updateUrlHash(${day}, ${timeZone})`);
  const normalizedDay = getNormalizedDayForHash(day);
  const isConferenceTimeZone = timeZone === timeZoneConference.value;
  const timeZoneToken = isConferenceTimeZone ? "" : getTimeZoneTokenForHash(timeZone);
  return setUrlHash(normalizedDay, timeZoneToken);
}

watch(
  () => props.daySelected,
  (newDaySelected) => {
    log(`daySelected has changed; updating URL hash`);
    return updateUrlHash(newDaySelected, props.timeZoneSelected);
  }
);

watch(route, (to, from) => {
  log(`program: change from '${from.name}' to '${to.name}'`);
  if (to.name.match(/program/i) && !from.name.match(/program/i)) {
    if (props.daySelected) {
      log(`A day is selected - updating URL hash`);
      updateUrlHash(props.daySelected, props.timeZoneSelected);
    } else {
      log(`No day selected - reset view`);
    }
  }
});
</script>

<style scoped>
#timezone {
  float: right;
  margin-top: 12px; /* same as App.vue/h3 */
  margin-bottom: 8px; /* same as App.vue/h3 */
  font-size: 14px;
  line-height: 33px;
  clear: right; /* https://stackoverflow.com/q/3415635 */
}
/* 25px tall, as Key Dates' (Seán, 2026-10-29); each page puts it on its heading's baseline */
#timezone :deep(.trigger) {
  line-height: 1.25;
  padding-block: 2.5px;
}
.narrow #timezone,
.narrow #timezone {
  margin-top: 0px; /* same as App.vue/h3 */
  margin-bottom: 14px; /* same as App.vue/h3 */
}
/* in the narrow sidebar's Program panel (the Masterclass page in one column), with no heading
   beside it: the panel's full width, outlined in the brand colour as the page's FAQ and Contact
   Us buttons, so it reads as a control, not a second row of tabs (Seán, 2026-10-29) */
.panel > #timezone {
  float: none;
  margin: 16px 0 0;
  line-height: normal;
}
.panel > #timezone :deep(.timeZoneInline),
.panel > #timezone :deep(.trigger) {
  width: 100%;
}
.panel > #timezone :deep(.trigger) {
  justify-content: center;
  height: 25px; /* as every time zone menu, not the tabs' 29px */
  background: var(--background-color);
  border-color: var(--brand-color);
}
</style>
